import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Plot from 'react-plotly.js';
import moment from 'moment';

import { Button, Modal } from 'antd';
import CVG_Background from 'data/cvg_background.png';

import { setBinDividers, setBinLabels, setChromaIsolines } from 'redux/actions';

import './index.scss';

const cog = {
  width: 1792,
  path: 'm 1024,640 q 0,106 -75,181 -75,75 -181,75 -106,0 -181,-75 -75,-75 -75,-181 0,-106 75,-181 75,-75 181,-75 106,0 181,75 75,75 75,181 z m 512,109 V 527 q 0,-12 -8,-23 -8,-11 -20,-13 l -185,-28 q -19,-54 -39,-91 35,-50 107,-138 10,-12 10,-25 0,-13 -9,-23 -27,-37 -99,-108 -72,-71 -94,-71 -12,0 -26,9 l -138,108 q -44,-23 -91,-38 -16,-136 -29,-186 -7,-28 -36,-28 H 657 q -14,0 -24.5,8.5 Q 622,-111 621,-98 L 593,86 q -49,16 -90,37 L 362,16 Q 352,7 337,7 323,7 312,18 186,132 147,186 q -7,10 -7,23 0,12 8,23 15,21 51,66.5 36,45.5 54,70.5 -27,50 -41,99 L 29,495 Q 16,497 8,507.5 0,518 0,531 v 222 q 0,12 8,23 8,11 19,13 l 186,28 q 14,46 39,92 -40,57 -107,138 -10,12 -10,24 0,10 9,23 26,36 98.5,107.5 72.5,71.5 94.5,71.5 13,0 26,-10 l 138,-107 q 44,23 91,38 16,136 29,186 7,28 36,28 h 222 q 14,0 24.5,-8.5 Q 914,1391 915,1378 l 28,-184 q 49,-16 90,-37 l 142,107 q 9,9 24,9 13,0 25,-10 129,-119 165,-170 7,-8 7,-22 0,-12 -8,-23 -15,-21 -51,-66.5 -36,-45.5 -54,-70.5 26,-50 41,-98 l 183,-28 q 13,-2 21,-12.5 8,-10.5 8,-23.5 z',
  ascent: 1500,
  descent: 0,
};

const hashtag = {
  width: 600,
  path: 'M440.667 182.109l7.143-40c1.313-7.355-4.342-14.109-11.813-14.109h-74.81l14.623-81.891C377.123 38.754 371.468 32 363.997 32h-40.632a12 12 0 0 0-11.813 9.891L296.175 128H197.54l14.623-81.891C213.477 38.754 207.822 32 200.35 32h-40.632a12 12 0 0 0-11.813 9.891L132.528 128H53.432a12 12 0 0 0-11.813 9.891l-7.143 40C33.163 185.246 38.818 192 46.289 192h74.81L98.242 320H19.146a12 12 0 0 0-11.813 9.891l-7.143 40C-1.123 377.246 4.532 384 12.003 384h74.81L72.19 465.891C70.877 473.246 76.532 480 84.003 480h40.632a12 12 0 0 0 11.813-9.891L151.826 384h98.634l-14.623 81.891C234.523 473.246 240.178 480 247.65 480h40.632a12 12 0 0 0 11.813-9.891L315.472 384h79.096a12 12 0 0 0 11.813-9.891l7.143-40c1.313-7.355-4.342-14.109-11.813-14.109h-74.81l22.857-128h79.096a12 12 0 0 0 11.813-9.891zM261.889 320h-98.634l22.857-128h98.634l-22.857 128z',
  ascent: 500,
  descent: 0,
};

const circle = {
  width: 600,
  path: 'M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200z',
  ascent: 500,
  descent: 0,
};

// const colors = ["#e41a1c", "#377eb8", "#4daf4a"];

/**
 * An array of the vector colors
 */

const vectorColors = [
  '#e32f32',
  '#e54f4f',
  '#fa843b',
  '#feb73c',
  '#cbcb50',
  '#7fb951',
  '#44c00b',
  '#109c7b',
  '#22bbaf',
  '#13a2bb',
  '#0f82c0',
  '#3c62a8',
  '#6d67ac',
  '#6a4e84',
  '#9d69a0',
  '#a65081',
];

export const CVGPlot = ({ numericalResults, active }) => {
  const dispatch = useDispatch();
  const location = window.location;

  const [showInfo, setShowInfo] = useState(false);

  const {
    path_xtest,
    path_ytest,
    path_xref,
    path_yref,
    rg: rgFull,
    rf: rfFull,
    CCT,
    DUV,
    local_chroma_shift: lcs,
    local_hue_shift: lhs,
    local_color_fidelity: lcf,
  } = numericalResults;

  let xtest = path_xtest;
  let ytest = path_ytest;
  let xref = path_xref;
  let yref = path_yref;

  if (xtest.length === 16) {
    xtest = [...xtest, xtest[0]];
    ytest = [...ytest, ytest[0]];
    xref = [...xref, xref[0]];
    yref = [...yref, yref[0]];
  }

  const bin_labels = useSelector((state) => state.dataReducers.bin_labels);
  const bin_dividers = useSelector((state) => state.dataReducers.bin_dividers);
  const chroma_isolines = useSelector(
    (state) => state.dataReducers.chroma_isolines
  );

  //Used to store the text of the hovertext
  let hoveredText = [];

  let rf = Math.round(rfFull);
  let rg = Math.round(rgFull);
  let cct = Math.round(CCT[0]);
  // let duv = Math.round(DUV[0] * 10000) / 10000;
  let arrowSize = 2;
  let rg_label = [1.35, 1.25];
  let rf_label = [-1.35, 1.25];
  let r_fh1 = [-1.3, -1.25];
  let r_csh1 = [1.25, -1.25];

  if (active === 'full') {
    arrowSize = 2;
    if (rg <= 99) {
      rg_label = [1.35, 1.25];
      rf_label = [-1.35, 1.25];
    } else {
      rg_label = [1.3, 1.25];
      rf_label = [-1.3, 1.25];
    }
    r_fh1 = [-1.3, -1.25];
    r_csh1 = [1.25, -1.25];
  } else if (active === 'intermediate') {
    arrowSize = 3;
    if (rg <= 99) {
      rg_label = [1.35, 1.25];
      rf_label = [-1.35, 1.25];
    } else {
      rg_label = [1.3, 1.25];
      rf_label = [-1.3, 1.25];
    }
    r_fh1 = [-1.3, -1.25];
    r_csh1 = [1.25, -1.25];
  } else {
    if (rg <= 99) {
      rg_label = [1.35, 1.25];
      rf_label = [-1.35, 1.25];
    } else {
      rg_label = [1.3, 1.25];
      rf_label = [-1.3, 1.25];
    }
    r_fh1 = [-1.3, -1.25];
    r_csh1 = [1.25, -1.25];
  }

  /**
   * Setting up the Reference Circles
   */
  let minus_20p_x = []; // -20% Ref Circle
  let minus_20p_y = [];
  let minus_10p_x = []; // -10% Ref Circle
  let minus_10p_y = [];
  let even_x = []; // 0% Ref Circle
  let even_y = [];
  let plus_10p_x = []; // +10% Ref Circle
  let plus_10p_y = [];
  let plus_20p_x = []; // +20% Ref Circle
  let plus_20p_y = [];
  let bin_lines = [];
  let chroma_isolines_labels = [];
  let black_x = [];
  let black_y = [];

  for (let theta = 1; theta <= 361; theta++) {
    black_x[theta] = Math.cos((theta * Math.PI) / 180);
    black_y[theta] = Math.sin((theta * Math.PI) / 180);
  }
  for (let i = 0; i < 16; i++) {
    let distance = Math.sqrt(
      Math.pow(xref[i] - xtest[i], 2) + Math.pow(yref[i] - ytest[i], 2)
    );
    if (distance.toFixed(3) <= 0.029) {
      if (xref[i] > xtest[i]) {
        xref[i] = xtest[i] + 0.04;
        yref[i] = ytest[i] + 0.04;
      } else {
        xref[i] = xtest[i] - 0.04;
        yref[i] = ytest[i] - 0.04;
      }
    }

    hoveredText[i] = `<i>R</i><sub>cs,h${i + 1}</sub>: ${lcs[i].toFixed(
      2
    )}% <br /><i>R</i><sub>hs,h${i + 1}</sub>: ${lhs[i].toFixed(
      2
    )} <br /><i>R</i><sub>f,h${i + 1}</sub>: ${lcf[i].toFixed(0)}`;
  }

  hoveredText[16] = `<i>R</i><sub>cs,h${1}</sub>: ${lcs[0].toFixed(
    2
  )}% <br /><i>R</i><sub>hs,h${1}</sub>: ${lhs[0].toFixed(
    2
  )} <br /><i>R</i><sub>f,h${1}</sub>: ${lcf[0].toFixed(0)}`;

  /**
   * Setting up the vector arrays
   */
  let arrows = [];
  let i_arrow;
  for (let i = 0; i < 16; i++) {
    i_arrow = {
      x: xtest[i],
      y: ytest[i],
      xref: 'x',
      yref: 'y',
      text: '',
      showarrow: true,
      arrowhead: 3,
      arrowwidth: arrowSize,
      axref: 'x',
      ayref: 'y',
      ax: xref[i],
      ay: yref[i],
      arrowcolor: vectorColors[i],
    };
    arrows.push(i_arrow);
  }

  const clearChromaIsolines = () => {
    minus_20p_x = []; // -20% Ref Circle
    minus_20p_y = [];
    minus_10p_x = []; // -10% Ref Circle
    minus_10p_y = [];
    even_x = []; // 0% Ref Circle
    even_y = [];
    plus_10p_x = []; // +10% Ref Circle
    plus_10p_y = [];
    plus_20p_x = []; // +20% Ref Circle
    plus_20p_y = [];
    chroma_isolines_labels = [];
  };

  /**
   * Setting up the bin lines
   */
  const createBinLines = () => {
    bin_lines = [
      {
        x: [-1.5, 1.5],
        y: [0, 0],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [-1.39, 1.39],
        y: [-0.57, 0.57],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [-1.06, 1.06],
        y: [-1.06, 1.06],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [-0.57, 0.57],
        y: [-1.39, 1.39],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [0, 0],
        y: [-1.5, 1.5],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [0.57, -0.57],
        y: [-1.39, 1.39],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [1.06, -1.06],
        y: [-1.06, 1.06],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [1.39, -1.39],
        y: [-0.57, 0.57],
        mode: 'lines',
        name: 'Solid',
        line: {
          dash: '5px',
          width: 1,
        },
        marker: { color: 'grey' },
        showlegend: false,
        hoverinfo: 'skip',
      },
    ];
  };

  /**
   * Setting up the Bin Numbers
   */
  let bin_numbers = [];
  const createBinNumbers = () => {
    bin_numbers = [];
    let theta = 11.5;
    for (let i = 1; i <= 16; i++) {
      var x = 1.35 * Math.cos((theta * Math.PI) / 180);
      var y = 1.35 * Math.sin((theta * Math.PI) / 180);
      theta += 22.5;
      bin_numbers[i - 1] = {
        x: x,
        y: y,
        xref: 'x',
        yref: 'y',
        text: `${i}`,
        font: {
          size: 16,
          color: 'grey',
        },
        ax: 0,
        ay: 0,
      };
    }
  };

  const createChromaIsolines = () => {
    for (let theta = 1; theta <= 361; theta++) {
      // -20% Ref Circle
      minus_20p_x[theta] = 0.8 * Math.cos((theta * Math.PI) / 180);
      minus_20p_y[theta] = 0.8 * Math.sin((theta * Math.PI) / 180);

      // -10% Ref Circle
      minus_10p_x[theta] = 0.9 * Math.cos((theta * Math.PI) / 180);
      minus_10p_y[theta] = 0.9 * Math.sin((theta * Math.PI) / 180);

      // 0% Ref Circle
      even_x[theta] = Math.cos((theta * Math.PI) / 180);
      even_y[theta] = Math.sin((theta * Math.PI) / 180);

      // +10% Ref Circle
      plus_10p_x[theta] = 1.1 * Math.cos((theta * Math.PI) / 180);
      plus_10p_y[theta] = 1.1 * Math.sin((theta * Math.PI) / 180);

      //+20% Ref Circle
      plus_20p_x[theta] = 1.2 * Math.cos((theta * Math.PI) / 180);
      plus_20p_y[theta] = 1.2 * Math.sin((theta * Math.PI) / 180);
    }

    chroma_isolines_labels = [
      {
        x: -0.15,
        y: -0.7,
        xref: 'x',
        yref: 'y',
        text: `-20%`,
        font: {
          size: 12,
          color: 'white',
        },
        ax: 0,
        ay: 0,
      },
      {
        x: -0.16,
        y: -1.24,
        xref: 'x',
        yref: 'y',
        text: `+20%`,
        font: {
          size: 12,
          color: 'white',
        },
        ax: 0,
        ay: 0,
      },
    ];
  };

  if (bin_labels) {
    createBinLines();
  } else {
    bin_lines = [];
  }

  if (bin_dividers) {
    createBinNumbers();
  } else {
    bin_numbers = [];
  }
  if (chroma_isolines) {
    createChromaIsolines();
  } else {
    clearChromaIsolines();
  }

  const InfoModal = (
    <Modal
      width={700}
      title='Color Vector Graphic'
      visible={showInfo}
      onCancel={() => setShowInfo(false)}
      footer={[
        <Button key='close' onClick={() => setShowInfo(false)}>
          Close
        </Button>,
      ]}
    >
      <p>
        The Color Vector Graphic (CVG), a visual representation of gamut shape,
        is a normalized version of the average change in (a', b') coordinates of
        CAM02-UCS for the CES within each hue-angle bin. Shifts in the radial
        direction indicate chroma change, whereas shifts tangential to the
        reference circle indicate hue changes. The colored background is for
        visual orientation only.
      </p>
      <p>
        This version of the CVG includes values for additional TM-30 measures:
      </p>
      <ul>
        <li>
          <i>R</i>
          <sub>f</sub> indicates the average magnitude of difference between the
          test light source and reference illuminant. Values closer to 100
          indicate greater similarity to the reference.
        </li>
        <li>
          <i>R</i>
          <sub>g</sub> approximates the average change in chroma. Values above
          100 represent an average increases, whereas values less than 100
          indicate an average decrease.
        </li>
        <li>
          <i>R</i>
          <sub>cs,h1</sub> characterizes the relative change in chroma for
          nominal red surfaces. Values greater than 0% indicate an increase in
          chroma, whereas values less than 0% indicate a decrease in chroma
          compared to the reference illuminant.
        </li>
        <li>
          <i>R</i>
          <sub>f,h1</sub> characterizes the magnitude of difference between the
          test light source and reference illuminant for nominally red surfaces.
          Values closer to 100 indicate greater similarity to the reference.
        </li>
        <li>
          PVF is a code to identify the performance using the specification
          criteria defined in TM-30 Annex E Table 2. P is for preference, V is
          for vividness, and F is for fidelity. The levels in ascending order
          are -, 3, 2, 1.
        </li>
      </ul>
    </Modal>
  );

  let preference;
  let vividness;
  let fidelity;

  const r_csh1_value = Math.round(lcs[0]);

  if (rf >= 78 && rg >= 95 && r_csh1_value >= -1 && r_csh1_value <= 15) {
    preference = 'P1';
  } else if (rf >= 75 && rg >= 92 && r_csh1_value >= -7 && r_csh1_value <= 19) {
    preference = 'P2';
  } else if (
    rf >= 70 &&
    rg >= 89 &&
    r_csh1_value >= -12 &&
    r_csh1_value <= 23
  ) {
    preference = 'P3';
  } else {
    preference = 'P-';
  }

  if (rg >= 118 && r_csh1_value >= 15) {
    vividness = 'V1';
  } else if (rg >= 110 && r_csh1_value >= 6) {
    vividness = 'V2';
  } else if (rg >= 100 && r_csh1_value >= 0) {
    vividness = 'V3';
  } else {
    vividness = 'V-';
  }

  if (rf >= 95) {
    fidelity = 'F1';
  } else if (rf >= 90 && Math.round(lcf[0]) >= 90) {
    fidelity = 'F2';
  } else if (rf >= 85 && Math.round(lcf[0]) >= 85) {
    fidelity = 'F3';
  } else {
    fidelity = 'F-';
  }

  const footerInfo = (
    <div>
      <p style={{ margin: 0 }}>
        CCT:{' '}
        <span style={{ fontWeight: '600', marginLeft: '8px', float: 'right' }}>
          {cct} K
        </span>
      </p>
      <p style={{ margin: 0 }}>
        <i>D</i>
        <sub>uv</sub> :{' '}
        <span style={{ fontWeight: '600', marginLeft: '8px', float: 'right' }}>
          {DUV[0].toFixed(4)}
        </span>
      </p>
    </div>
  );

  return (
    <div style={{ width: 'fit-content' }}>
      <div
        style={{
          color: 'black',
          display: 'flex',
          justifyContent: 'space-between',
          fontWeight: '600',
          marginBottom: 0,
          fontSize: '3rem',
        }}
      >
        {active === 'full' ? (
          <div style={{ fontWeight: 'normal', fontSize: '18px' }}>
            {footerInfo}{' '}
          </div>
        ) : (
          <p style={{ marginBottom: '4px', fontSize: '24px' }}>
            ANSI/IES TM-30-20
          </p>
        )}
        <p style={{ margin: '0 -3px 4px 0', fontSize: '24px' }}>
          {preference} {vividness} {fidelity}
        </p>
      </div>
      {/* <div className="cvg-container">
          <div className="cvg-graph"> */}
      <div>
        <Plot
          className='cvg-plot'
          useResizeHandler={true}
          style={{ width: '100%' }}
          data={[
            ...bin_lines,
            {
              x: [0],
              y: [0],
              type: 'scatter',
              mode: 'markers+lines',
              marker: {
                size: 22,
                width: 50,
                color: 'white',
                symbol: 'circle',
                opacity: 0.8,
              },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              x: [0],
              y: [0],
              type: 'scatter',
              mode: 'markers+lines',
              marker: {
                size: 30,
                width: 50,
                color: 'white',
                symbol: 'circle',
                opacity: 0.7,
              },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              x: [0],
              y: [0],
              type: 'scatter',
              mode: 'markers',
              marker: {
                size: 8,
                width: 50,
                color: 'grey',
                symbol: 'cross-thin-open',
              },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              name: '-20%',
              x: minus_20p_x,
              y: minus_20p_y,
              type: 'scatter',
              line: { shape: 'spline', smoothing: 1.3 },
              mode: 'lines+points',
              connectgaps: true,
              marker: { color: 'white' },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              name: '-10%',
              x: minus_10p_x,
              y: minus_10p_y,
              type: 'scatter',
              line: { shape: 'spline', smoothing: 1.3 },
              mode: 'lines+points',
              connectgaps: true,
              marker: { color: 'white' },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              name: '0%',
              x: black_x,
              y: black_y,
              type: 'scatter',
              line: { shape: 'spline', smoothing: 1.3 },
              mode: 'lines+points',
              connectgaps: true,
              marker: { color: 'black' },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              name: '10%',
              x: plus_10p_x,
              y: plus_10p_y,
              type: 'scatter',
              line: { shape: 'spline', smoothing: 1.3 },
              mode: 'lines+points',
              connectgaps: true,
              marker: { color: 'white' },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              name: '20%',
              x: plus_20p_x,
              y: plus_20p_y,
              type: 'scatter',
              line: { shape: 'spline', smoothing: 1.3 },
              mode: 'lines+points',
              connectgaps: true,
              marker: { color: 'white' },
              showlegend: false,
              hoverinfo: 'skip',
            },
            {
              text: hoveredText,
              customdata: hoveredText,
              name: '',
              x: xtest,
              y: ytest,
              type: 'scatter',
              line: { shape: 'spline', smoothing: 1.3, width: 4 },
              mode: 'lines+points',
              connectgaps: true,
              marker: { color: 'red' },
              hovertemplate: '%{customdata}',
            },
          ]}
          layout={{
            showlegend: false,
            hoverlabel: { bgcolor: '#FFF' },
            // autosize: true,
            autosize: false,
            width: 500,
            height: 500,
            margin: {
              t: 0,
              l: 0,
              b: 0,
              r: 0,
            },
            xaxis: {
              visible: false,
              range: [-1.5, 1.5],
              title: {
                text: '',
              },
            },
            yaxis: {
              visible: false,
              range: [-1.5, 1.5],
              title: {
                text: '',
              },
              tickformat: '',
            },
            images: [
              {
                source: CVG_Background,
                xref: 'x',
                yref: 'y',
                x: -1.5,
                y: 1.5,
                sizex: 3,
                sizey: 3,
                sizing: 'stretch',
                layer: 'below',
              },
            ],
            annotations: [
              ...arrows,
              {
                x: rf_label[0],
                y: rf_label[1],
                xref: 'x',
                yref: 'y',
                text: `<b>${rf}</b><br /><i>R</i><sub>f</sub>`,
                font: {
                  size: 22,
                  color: 'black',
                },
                align: 'left',
                ax: 0,
                ay: 0,
              },
              {
                x: rg_label[0],
                y: rg_label[1],
                xref: 'x',
                yref: 'y',
                text: `<b>${rg}</b><br /><i>R</i><sub>g</sub>`,
                font: {
                  size: 22,
                  color: 'black',
                },
                align: 'right',
                ax: 0,
                ay: 0,
              },
              {
                x: r_fh1[0],
                y: r_fh1[1],
                xref: 'x',
                yref: 'y',
                text: `<i>R</i><sub>f,h1</sub><br /><b>${Math.round(lcf[0])}`,
                font: {
                  size: 22,
                  color: 'black',
                },
                align: 'right',
                ax: 0,
                ay: 0,
              },
              {
                x: r_csh1[0],
                y: r_csh1[1],
                xref: 'x',
                yref: 'y',
                text: `<i>R</i><sub>cs,h1</sub><br><b>${Math.round(lcs[0])}%`,
                font: {
                  size: 22,
                  color: 'black',
                },
                align: 'left',
                ax: 0,
                ay: 0,
              },
              ...chroma_isolines_labels,
              ...bin_numbers,
            ],
          }}
          config={{
            displaylogo: false,
            modeBarButtons: [
              [
                'toImage',
                {
                  name: 'Info',
                  icon: '',
                  click: () => setShowInfo(true),
                },
              ],

              [
                {
                  name: 'Bin Dividers',
                  icon: cog,
                  click: () => dispatch(setBinLabels()),
                },
              ],
              [
                {
                  name: 'Bin Labels',
                  icon: hashtag,
                  click: () => dispatch(setBinDividers()),
                },
              ],
              [
                {
                  name: 'Chroma Isolines',
                  icon: circle,
                  click: () => dispatch(setChromaIsolines()),
                },
              ],
            ],
            responsive: true,
            toImageButtonOptions: {
              format: 'png', // one of png, svg, jpeg, webp
              filename: 'Color Vector Graph',
              height: 500,
              width: 500,
              scale: 1.5, // Multiply title/legend/axis/canvas sizes by this factor
            },
          }}
        />
      </div>
      {/* </div>
        </div> */}
      {active !== 'full' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '18px',
            fontWeight: '500',
            marginTop: '6px',
            color: 'black',
          }}
        >
          {window.location != window.parent.location && active === 'simple' ? (
            <div
              style={{
                display: 'flex',
                inlineSize: 400,
                marginRight: 25,
                overflowWrap: 'break-word',
              }}
            >
              <img
                alt='app-logo'
                style={{ width: 40, height: 40, marginRight: 10 }}
                src={`${process.env.PUBLIC_URL}/images/ies-logo.jpg`}
              />
              <p style={{ fontSize: '10px' }}>
                The content of this report was generated by the Illuminating
                Engineering Society’s online spectral calculator (
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href='https://www.ies.org/spectral-calculator'
                >
                  www.ies.org/spectralcalculator
                </a>
                ), in conformance with the ANSI/IES TM-30-20 standard published
                by the IES, 120 Wall Street, New York, New York 10005.
              </p>
            </div>
          ) : (
            <div>
              <p style={{ margin: 0 }}>PNNL Spectral Calculator</p>
              <p
                style={{
                  margin: active === 'intermediate' ? '0 0 0 -34px' : 0,
                }}
              >
                Created {moment().format('l')}
              </p>
            </div>
          )}

          <div>{footerInfo}</div>
        </div>
      )}
      {InfoModal}
    </div>
  );
};

CVGPlot.propTypes = {
  numericalResults: PropTypes.object,
  active: PropTypes.string,
};
